import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import SelectedPortableItem from '~/components/SelectedPortableItem';
import UpdateBlanketCoverItemModal from '~/components/UpdateBlanketCoverItemModal';
import LinkButton from '~/components-ds/LinkButton';
import {
  COVER_CATEGORIES,
  SPECIFIED_ITEM_LIMIT,
  TOTAL_COVER_VALUE_LIMIT,
} from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useBusinessDetails, useCoverDetails } from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { updateBlanketCovers } from '~/slices/coverDetails';
import { qalFormStartAction, qalTrackEngagementAction } from '~/thunks/analytics';
import { CoverItem, CoverTypes } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

const serialNumber = CoverTypes.GENERAL;

const BlanketCover: React.FC = () => {
  const dispatch = useAppDispatch();
  const { industry } = useBusinessDetails();
  const { blanketCovers, totalCoverValue } = useCoverDetails();
  const specifiedItemLimit = industry?.limits?.specifiedItemMin || SPECIFIED_ITEM_LIMIT;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCover, setSelectedCover] = useState<CoverItem>();

  const isMaxLimitError = totalCoverValue !== null && totalCoverValue > TOTAL_COVER_VALUE_LIMIT;

  const handleDelete = (id: string) => {
    if (blanketCovers) {
      const updatedList = [...blanketCovers].filter((item) => item.id !== id);
      dispatch(updateBlanketCovers(updatedList));
    }
  };

  const handleEdit = (item: CoverItem) => {
    setSelectedCover({
      id: item.id,
      code: item.code,
      category: item.category,
      value: item.value,
      serialNumber,
    });
    setIsModalOpen(true);
  };

  const onCloseHandler = () => {
    setSelectedCover(undefined);
    setIsModalOpen(false);
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'modal: close',
        eventLabel: 'add blanket cover',
        data: {},
      })
    );
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsPortableItems',
    tokens: {
      specifiedItemLimit: () => currencyFormatter(specifiedItemLimit - 1, CurrencyFormat.Comma),
    },
  });

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();
  return (
    <>
      <Typography
        variant="subtitle2"
        component="h3"
        fontWeight="fontWeightBold"
        sx={{ marginBottom: 1 }}
      >
        {getTextContent('content.blanketCover.title')}
      </Typography>

      <Typography align="center" fontWeight="fontWeightMedium" sx={{ marginBottom: 2 }}>
        {getJSXContent('content.blanketCover.description')}
      </Typography>

      {blanketCovers && (
        <Box mt={2} mb={2} maxWidth="400px" width="100%">
          <SelectedPortableItem
            coverList={blanketCovers}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Box>
      )}

      {blanketCovers?.length !== COVER_CATEGORIES.length && (
        <LinkButton
          label={getTextContent('buttons.addCategory')}
          disabled={isMaxLimitError}
          onClick={() => {
            setIsModalOpen(true);
            handleFormStart();
          }}
          muiIcon={AddCircleIcon}
        />
      )}

      {/* Note: Not using open prop on modal to clear modal data after modal closed */}
      {isModalOpen && (
        <UpdateBlanketCoverItemModal
          serialNumber={serialNumber}
          selectedCover={selectedCover}
          onUpdate={updateBlanketCovers}
          onClose={onCloseHandler}
        />
      )}
    </>
  );
};

export default BlanketCover;
